'use client';

import { useEffect, useState } from 'react';
import { RotateCcw, Download } from 'lucide-react';

const STORAGE_KEY = 'planetroadmap.dora-metrics-calculator.v1';

type DoraInputs = {
  // Deployment Frequency
  deploysPerWeek: number; // average per week
  // Lead Time for Changes (commit -> production), in hours
  leadTimeHours: number;
  // Mean Time to Restore (incident detected -> resolved), in hours
  mttrHours: number;
  // Change Failure Rate (% of deploys causing incident/rollback)
  changeFailureRatePct: number;
};

function defaults(): DoraInputs {
  return {
    deploysPerWeek: 5,
    leadTimeHours: 48,
    mttrHours: 4,
    changeFailureRatePct: 15,
  };
}

// Source: DORA's "State of DevOps" performance categories.
// Boundaries follow the published thresholds for Elite / High / Medium / Low.
type Tier = 'Elite' | 'High' | 'Medium' | 'Low';
const TIER_COLORS: Record<Tier, string> = {
  Elite: 'text-emerald-700 dark:text-emerald-400',
  High: 'text-primary',
  Medium: 'text-amber-700 dark:text-amber-400',
  Low: 'text-red-700 dark:text-red-400',
};

function deployFrequencyTier(deploysPerWeek: number): Tier {
  // Elite: multiple per day (≥7/week is "daily or more")
  if (deploysPerWeek >= 7) return 'Elite';
  // High: between once per week and once per day
  if (deploysPerWeek >= 1) return 'High';
  // Medium: between once per month and once per week (~0.25-1)
  if (deploysPerWeek >= 0.25) return 'Medium';
  return 'Low';
}

function leadTimeTier(hours: number): Tier {
  // Elite: < 24 hours (< 1 day)
  if (hours < 24) return 'Elite';
  // High: 1 day - 1 week
  if (hours < 24 * 7) return 'High';
  // Medium: 1 week - 1 month
  if (hours < 24 * 30) return 'Medium';
  return 'Low';
}

function mttrTier(hours: number): Tier {
  // Elite: < 1 hour
  if (hours < 1) return 'Elite';
  // High: < 1 day
  if (hours < 24) return 'High';
  // Medium: < 1 week
  if (hours < 24 * 7) return 'Medium';
  return 'Low';
}

function cfrTier(pct: number): Tier {
  // Elite: 0-5%
  if (pct <= 5) return 'Elite';
  // High: 5-10%
  if (pct <= 10) return 'High';
  // Medium: 10-15%
  if (pct <= 15) return 'Medium';
  return 'Low';
}

function fmtLeadTime(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  if (hours < 24 * 7) return `${(hours / 24).toFixed(1)} days`;
  return `${(hours / (24 * 7)).toFixed(1)} weeks`;
}

function fmtDeployFreq(deploysPerWeek: number): string {
  if (deploysPerWeek >= 7) return `${(deploysPerWeek / 7).toFixed(1)}/day`;
  if (deploysPerWeek >= 1) return `${deploysPerWeek.toFixed(1)}/week`;
  if (deploysPerWeek >= 0.23) return `${(deploysPerWeek * 4.33).toFixed(1)}/month`;
  return `${(deploysPerWeek * 52).toFixed(1)}/year`;
}

export function DoraMetricsCalculator() {
  const [inputs, setInputs] = useState<DoraInputs>(defaults());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DoraInputs;
        if (parsed && typeof parsed.deploysPerWeek === 'number') {
          setInputs(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setInputs(defaults());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [inputs, hydrated]);

  function update(patch: Partial<DoraInputs>) {
    setInputs((curr) => ({ ...curr, ...patch }));
  }
  function reset() {
    if (!confirm('Reset to defaults?')) return;
    setInputs(defaults());
  }

  const dfTier = deployFrequencyTier(inputs.deploysPerWeek);
  const ltTier = leadTimeTier(inputs.leadTimeHours);
  const mtTier = mttrTier(inputs.mttrHours);
  const cfTier = cfrTier(inputs.changeFailureRatePct);

  const tierOrder: Record<Tier, number> = { Elite: 4, High: 3, Medium: 2, Low: 1 };
  const overall = [dfTier, ltTier, mtTier, cfTier]
    .map((t) => tierOrder[t])
    .reduce((a, b) => a + b, 0);
  const avg = overall / 4;
  const overallTier: Tier = avg >= 3.5 ? 'Elite' : avg >= 2.5 ? 'High' : avg >= 1.5 ? 'Medium' : 'Low';

  function exportCsv() {
    const rows = [
      ['Metric', 'Value', 'Tier'],
      ['Deployment Frequency', fmtDeployFreq(inputs.deploysPerWeek), dfTier],
      ['Lead Time for Changes', fmtLeadTime(inputs.leadTimeHours), ltTier],
      ['Mean Time to Restore', fmtLeadTime(inputs.mttrHours), mtTier],
      ['Change Failure Rate', `${inputs.changeFailureRatePct}%`, cfTier],
      ['', '', ''],
      ['Overall', '', overallTier],
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dora-metrics.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading calculator…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className={`rounded-2xl p-6 text-center border ${
          overallTier === 'Elite' || overallTier === 'High'
            ? 'bg-primary/5 border-primary/20'
            : overallTier === 'Medium'
              ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40'
              : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40'
        }`}
      >
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Overall DORA performance
        </div>
        <div className={`text-4xl font-bold ${TIER_COLORS[overallTier]}`}>{overallTier}</div>
        <div className="text-xs text-muted-foreground mt-2">
          Based on the 2024 State of DevOps performance categories.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <MetricCard
          title="Deployment Frequency"
          subtitle="How often you deploy to production"
          tier={dfTier}
          formatted={fmtDeployFreq(inputs.deploysPerWeek)}
        >
          <div className="flex items-center gap-3 text-sm">
            <input
              type="number"
              min={0}
              step={0.1}
              value={inputs.deploysPerWeek}
              onChange={(e) =>
                update({ deploysPerWeek: Math.max(0, Number(e.target.value)) })
              }
              className="w-24 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className="text-muted-foreground">deploys per week</span>
          </div>
          <TierGuide
            entries={[
              ['Elite', 'On-demand (≥1/day)'],
              ['High', 'Once/week to once/day'],
              ['Medium', 'Once/month to once/week'],
              ['Low', 'Less than once/month'],
            ]}
            current={dfTier}
          />
        </MetricCard>

        <MetricCard
          title="Lead Time for Changes"
          subtitle="Commit → production"
          tier={ltTier}
          formatted={fmtLeadTime(inputs.leadTimeHours)}
        >
          <div className="flex items-center gap-3 text-sm">
            <input
              type="number"
              min={0}
              step={0.5}
              value={inputs.leadTimeHours}
              onChange={(e) =>
                update({ leadTimeHours: Math.max(0, Number(e.target.value)) })
              }
              className="w-24 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className="text-muted-foreground">hours (median)</span>
          </div>
          <TierGuide
            entries={[
              ['Elite', '< 1 day'],
              ['High', '1 day – 1 week'],
              ['Medium', '1 week – 1 month'],
              ['Low', '> 1 month'],
            ]}
            current={ltTier}
          />
        </MetricCard>

        <MetricCard
          title="Mean Time to Restore"
          subtitle="Incident detected → resolved"
          tier={mtTier}
          formatted={fmtLeadTime(inputs.mttrHours)}
        >
          <div className="flex items-center gap-3 text-sm">
            <input
              type="number"
              min={0}
              step={0.25}
              value={inputs.mttrHours}
              onChange={(e) => update({ mttrHours: Math.max(0, Number(e.target.value)) })}
              className="w-24 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className="text-muted-foreground">hours (median)</span>
          </div>
          <TierGuide
            entries={[
              ['Elite', '< 1 hour'],
              ['High', '< 1 day'],
              ['Medium', '< 1 week'],
              ['Low', '> 1 week'],
            ]}
            current={mtTier}
          />
        </MetricCard>

        <MetricCard
          title="Change Failure Rate"
          subtitle="% of deploys causing incident/rollback"
          tier={cfTier}
          formatted={`${inputs.changeFailureRatePct}%`}
        >
          <div className="flex items-center gap-3 text-sm">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={inputs.changeFailureRatePct}
              onChange={(e) =>
                update({
                  changeFailureRatePct: Math.max(0, Math.min(100, Number(e.target.value))),
                })
              }
              className="w-24 text-right border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className="text-muted-foreground">percent</span>
          </div>
          <TierGuide
            entries={[
              ['Elite', '0–5%'],
              ['High', '5–10%'],
              ['Medium', '10–15%'],
              ['Low', '> 15%'],
            ]}
            current={cfTier}
          />
        </MetricCard>
      </div>

      <div className="flex flex-wrap gap-3 justify-end items-center">
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
        <button
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}

function MetricCard({
  title,
  subtitle,
  tier,
  formatted,
  children,
}: {
  title: string;
  subtitle: string;
  tier: Tier;
  formatted: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div className={`text-xs font-semibold ${TIER_COLORS[tier]}`}>{tier}</div>
      </div>
      <div className="text-2xl font-bold font-mono">{formatted}</div>
      {children}
    </div>
  );
}

function TierGuide({ entries, current }: { entries: [Tier, string][]; current: Tier }) {
  return (
    <div className="text-[11px] grid grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-border/40">
      {entries.map(([tier, range]) => (
        <div key={tier} className="flex justify-between gap-2">
          <span
            className={`font-semibold ${tier === current ? TIER_COLORS[tier] : 'text-muted-foreground'}`}
          >
            {tier}
          </span>
          <span className="text-muted-foreground">{range}</span>
        </div>
      ))}
    </div>
  );
}
